import { ClientLogger } from "@/lib/clientLogger";
import { useApprovalStore } from "@/components/custom/stores/approvalStore";
import type { ThreadHistoryItem } from "./threadHistoryProvider";

// `openPreviewInSidebar` lives behind a lazy import to avoid pulling
// `pdfDocumentStore` (and its module-level `getRaw(StorageKeys.pdfPanelWidth)` call)
// into every test that touches threadHistoryLoad. Production callers resolve it
// the first time they trigger hydration; test callers inject their own `openPreview`
// via the deps object and never reach this branch.
type OpenPreviewFn = (previewId: string, title?: string, threadId?: string) => Promise<void> | void;
let cachedOpenPreview: OpenPreviewFn | null = null;
async function defaultOpenPreview(previewId: string, title?: string, threadId?: string): Promise<void> {
  if (cachedOpenPreview === null) {
    const mod = await import("@/lib/threadDocumentOps");
    cachedOpenPreview = mod.openPreviewInSidebar;
  }
  await cachedOpenPreview(previewId, title, threadId);
}

/**
 * Phase 2 cold-reload re-hydration (ADR-042 / notes-approval-flow-fixes.md §N2).
 *
 * When a thread is loaded from history that contains a pending
 * `request_commit_approval` tool-call part (empty content = awaiting verdict),
 * the live `data-pre-approval-review` SSE event that originally populated
 * `useApprovalStore` is long gone. Without this hydration the SPA renders the
 * pending tool-call part but the sidebar approval row does not appear because
 * `useApprovalStore.pendingByPreviewId` is empty.
 *
 * The previewId is NOT on the persisted `ToolCallMessagePart.ArgumentsJson`
 * (it's allocated inside the AIFunction body AFTER `FunctionInvocationMiddleware`
 * snapshots the args — see ADR-042 + the Phase 1 root-cause analysis). Instead
 * we look it up server-side via `GET /api/preview/by-tool-call/{toolCallId}`,
 * which the backend resolves through `IPendingApprovalStore.TryGetByToolCallId`.
 *
 * **API restart caveat:** today `IPendingApprovalStore` is singleton in-memory.
 * After an API restart the by-tool-call lookup returns 404 (TTL expiry has the
 * same effect via the janitor). Phase 3 lifts this by persisting the store to
 * a `PendingApprovals` table.
 *
 * Fire-and-forget: the main thread load returns the rendered history immediately;
 * approval hydration races in the background. The sidebar opens slightly after
 * the chat history paints — acceptable UX trade-off for not blocking the page.
 */
export interface ApprovalHydrationDeps {
  apiAuthFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  /** Side-effect: stamp the store. Defaulted to the live useApprovalStore in production. */
  setPending?: (preview: ApprovalHydrationEntry) => void;
  /** Side-effect: open the sidebar to the staged preview. Defaulted to openPreviewInSidebar. */
  openPreview?: (previewId: string, title?: string, threadId?: string) => Promise<void> | void;
}

export interface ApprovalHydrationEntry {
  previewId: string;
  toolCallId: string;
  threadId: string;
  summary?: string;
  pdfUrl: string;
  docxUrl: string;
  diffHtmlUrl?: string;
  action?: string;
  documentName?: string;
  praetorDocumentId?: string;
  generatedDocumentId?: string;
}

interface PreviewByToolCallResponseShape {
  previewId: string;
  toolCallId: string;
  threadId: string;
  summary?: string | null;
  pdfUrl: string;
  docxUrl: string;
  diffHtmlUrl?: string | null;
  action?: string | null;
  documentName?: string | null;
  praetorDocumentId?: string | null;
}

const REQUEST_COMMIT_APPROVAL = "request_commit_approval";

/**
 * Scan history for pending `request_commit_approval` parts and (a) re-stamp the
 * approval store via `setPending` for each whose backend lookup succeeds, and
 * (b) auto-reopen the sidebar for the most-recently-emitted one — matching the
 * live SSE path's UX. 404s are silent (TTL / restart / cross-user) so a single
 * dropped previewId doesn't poison the chat-history rendering.
 *
 * Returns the list of hydrated entries (useful for tests; production callers
 * ignore the return).
 */
export async function hydrateApprovalStoreFromHistory(
  threadId: string,
  messages: readonly ThreadHistoryItem[],
  deps: ApprovalHydrationDeps,
): Promise<ApprovalHydrationEntry[]> {
  // Collect (toolCallId, messageIndex) tuples so we can pick the latest by chat order.
  const pendingCandidates: Array<{ toolCallId: string; orderHint: number }> = [];
  messages.forEach((msg, msgIdx) => {
    if (msg.role !== "assistant") return;
    for (const part of msg.parts) {
      if (part.type !== "tool_call") continue;
      if (part.functionName !== REQUEST_COMMIT_APPROVAL) continue;
      if ((part.content ?? "") !== "") continue; // non-empty == approved/rejected
      if (!part.toolCallId) continue;
      pendingCandidates.push({ toolCallId: part.toolCallId, orderHint: msgIdx });
    }
  });

  if (pendingCandidates.length === 0) return [];

  const setPending = deps.setPending ?? ((preview) => useApprovalStore.getState().setPending(preview));
  const openPreview = deps.openPreview ?? defaultOpenPreview;

  const results = await Promise.allSettled(
    pendingCandidates.map(async (candidate) => {
      const res = await deps.apiAuthFetch(`/api/preview/by-tool-call/${candidate.toolCallId}`);
      if (!res.ok) {
        // 404 is expected after TTL / API restart; anything else is logged as a soft warning.
        if (res.status !== 404) {
          ClientLogger.warn("Approval re-hydration: unexpected status", {
            component: "hydrateApprovalStoreFromHistory",
            metadata: { toolCallId: candidate.toolCallId, status: res.status },
          });
        }
        return null;
      }
      const data: PreviewByToolCallResponseShape = await res.json();
      const entry: ApprovalHydrationEntry = {
        previewId: data.previewId,
        toolCallId: data.toolCallId,
        threadId: data.threadId,
        summary: data.summary ?? undefined,
        pdfUrl: data.pdfUrl,
        docxUrl: data.docxUrl,
        diffHtmlUrl: data.diffHtmlUrl ?? undefined,
        action: data.action ?? undefined,
        documentName: data.documentName ?? undefined,
        praetorDocumentId: data.praetorDocumentId ?? undefined,
      };
      setPending(entry);
      return { entry, orderHint: candidate.orderHint };
    }),
  );

  // Collect successful entries with their orderHints so we can pick the latest.
  type Hydrated = { entry: ApprovalHydrationEntry; orderHint: number };
  const hydrated: Hydrated[] = [];
  for (const r of results) {
    if (r.status === "fulfilled" && r.value !== null) hydrated.push(r.value);
  }

  if (hydrated.length === 0) return [];

  // Pick the most recently emitted (largest orderHint) as the one to auto-open. This
  // mirrors the live SSE path where the sidebar auto-opens on the most recent
  // `data-pre-approval-review`. If the assistant emitted two pending approvals in
  // sequence (rare; usually only the LATEST has a live store entry post-Phase-1
  // backend evict, but we defend against the race anyway), the user sees the latest.
  hydrated.sort((a, b) => b.orderHint - a.orderHint);
  const latest = hydrated[0].entry;
  void Promise.resolve(openPreview(latest.previewId, latest.summary, threadId));

  return hydrated.map((h) => h.entry);
}
